import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import Navbar from "@/components/Navbar";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking, success, failed, expired
  const [planId, setPlanId] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPlanId(data.plan_id);
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 8) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        attempts.current += 1;
        if (attempts.current >= 8) setStatus("failed");
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-6 lg:px-8 py-20">
        <Card className="bg-white border border-gray-200 rounded-2xl">
          <CardContent className="p-12 text-center">
            {status === "checking" && (
              <>
                <Loader2 className="h-8 w-8 text-slate-500 animate-spin mx-auto" />
                <h1 className="mt-5 text-2xl font-medium tracking-tight">Bekrefter betalingen din...</h1>
                <p className="mt-3 text-slate-500">Dette tar normalt bare et par sekunder.</p>
              </>
            )}
            {status === "success" && (
              <>
                <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <CheckCircle2 className="h-7 w-7" />
                </div>
                <h1 className="mt-5 text-3xl font-medium tracking-tight" data-testid="success-title">Takk! Planen er låst opp.</h1>
                <p className="mt-3 text-slate-600">Du har nå tilgang til hele forretningsplanen og PDF-eksport.</p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={() => navigate(`/plan/${planId}/gratulerer`)} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="success-view-plan">
                    Se neste steg
                  </Button>
                  <Button variant="outline" onClick={() => navigate(`/plan/${planId}`)} className="rounded-full">
                    Åpne forretningsplan
                  </Button>
                </div>
              </>
            )}
            {(status === "failed" || status === "expired") && (
              <>
                <div className="mx-auto h-14 w-14 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center">
                  <XCircle className="h-7 w-7" />
                </div>
                <h1 className="mt-5 text-2xl font-medium tracking-tight">Betalingen ble ikke fullført</h1>
                <p className="mt-3 text-slate-600">Det kan hende økten utløp, eller noe gikk galt. Du kan prøve igjen.</p>
                <Button onClick={() => navigate("/dashboard")} className="mt-8 rounded-full bg-slate-900 hover:bg-slate-800">
                  Tilbake til dashbord
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
